import { useEffect, useState } from "react";
import { getAllReports, ReportItem } from "../api-request/api-request";
import { RangeValue } from "../components/DateRangePicker";
import { getDownloadFileUrl } from "../state/services";
import { InputParamState } from "../state/Input-param-state";
import { triggerFileDownload } from "../utils/utils";

interface UseReportsResult {
  reports: ReportItem[];
  loading: boolean;
  error: string | null;
}

export const useReports = (
  enabled: boolean,
  accessToken?: string,
  canAccess?: boolean,
): UseReportsResult => {
  const [reports, setReports] = useState<ReportItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!accessToken || !canAccess) return;
    if( !enabled ) return;

    let isMounted = true;


    const fetchReports = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await getAllReports(accessToken);

        if (!isMounted) return;

        if (result.status === "successful") {
          setReports(result.response ?? []);
        } else {
          setError("Failed to load reports.");
        }
      } catch (err) {
        if (!isMounted) return;
        setError("Something went wrong while fetching reports.");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchReports();

    return () => {
      isMounted = false;
    };
  }, [accessToken, canAccess, enabled]);

  return { reports, loading, error };
};

export interface DownloadReportRequest {
  code: string;
  start_date?: string;
  end_date?: string;
}

const formatDateToYMD = (date: Date): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");

  return `${year}-${month}-${day}T00:00:00`;
};

type DownloadParams = {
  item: ReportItem;
  range?: RangeValue;
  parameterValues?: Record<string, string>;
};

export function useReportDownload(accessToken: string) {
  const [downloadingCode, setDownloadingCode] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = async ({
    item,
    range,
    parameterValues = {},
  }: DownloadParams) => {
    if (item.date_range) {
      if (!range?.from || !range?.to) {
        setDownloadError("Please select a valid date range.");
        return;
      }
    }

    if (item.parameters?.length > 0) {
      for (const param of item.parameters) {
        if (!parameterValues[param.code]) {
          setDownloadError(`Please select ${param.label}.`);
          return;
        }
      }
    }

    setDownloadingCode(item.code);

    try {
      const request: Record<string, string> = {
        code: item.code,
      };

      if (item.date_range && range?.from && range?.to) {
        request.start_date = formatDateToYMD(range.from);
        request.end_date = formatDateToYMD(range.to);
      }

      if (item.parameters?.length > 0) {
        item.parameters.forEach((param) => {
          request[param.code] = parameterValues[param.code];
        });
      }

      const result = await getDownloadFileUrl(request, accessToken);

      if (result.status === "successful" && result.errors) {
        const message = result?.errors.message ?? "Something went wrong";
        setDownloadError(message);
        return;
      }

      if (result.status === "successful" && result.response?.file_url === '') {
        setDownloadError("No file found for the selected date range.");
      }

      if (result.status === "successful" && result.response?.file_url) {
        triggerFileDownload(result.response.file_url);
        return;
      }

      setDownloadError("Something went wrong.");
    } catch {
      setDownloadError("Failed to download report.");
    } finally {
      setDownloadingCode(null);
    }
  };

  return {
    downloadingCode,
    downloadError,
    setDownloadError,
    handleDownload,
  };
}